/**
 * One shared, frame-safe file save path for every export view.
 *
 * Anchor-based downloads are silently refused inside cross-origin embedded
 * frames (the Lovable preview, an iframe embed, some in-app browsers), which
 * makes an export look like it succeeded while nothing ever reaches the disk.
 * `saveFile` detects those environments, opens the file in a real tab instead,
 * and reports back which delivery route was used so the UI can tell the truth.
 */

/** Keep object URLs alive long enough for slow viewers to read them. */
const URL_TTL_MS = 60_000;

export type SaveMethod = "download" | "new-tab" | "manual";

export interface SavedFile {
  method: SaveMethod;
  /** Object URL for the saved file; stays valid for about a minute. */
  url: string;
  filename: string;
}

/** True when the app is rendered inside another document (preview/iframe). */
export function isEmbeddedFrame(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access threw — we are definitely framed.
    return true;
  }
}

function assertDeliverable(blob: Blob, filename: string) {
  if (!blob || blob.size === 0) throw new Error("The generated PDF was empty.");
  if (filename.toLowerCase().endsWith(".pdf") && blob.type && blob.type !== "application/pdf") {
    throw new Error("The generated file was not a valid PDF.");
  }
}

function clickAnchor(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => a.remove(), 150);
}

function openTab(url: string): boolean {
  try {
    const win = window.open(url, "_blank", "noopener,noreferrer");
    return !!win;
  } catch {
    return false;
  }
}

/**
 * Hands a file to the browser and reports how it was delivered.
 *
 * - `download` — a normal save-to-disk handoff.
 * - `new-tab` — framed context: the file opened in a new tab to save from.
 * - `manual` — the browser refused both; the caller should surface `url` as an
 *   "Open file" link so the user can still retrieve it.
 */
export async function saveFile(blob: Blob, filename: string): Promise<SavedFile> {
  if (typeof document === "undefined") {
    throw new Error("Downloads are only available in the browser.");
  }
  assertDeliverable(blob, filename);

  const url = URL.createObjectURL(blob);
  window.setTimeout(() => URL.revokeObjectURL(url), URL_TTL_MS);

  // Inside a frame the download attribute is commonly ignored, so go straight
  // to a real tab; fall back to the anchor when popups are blocked.
  if (isEmbeddedFrame()) {
    if (openTab(url)) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
      return { method: "new-tab", url, filename };
    }
    clickAnchor(url, filename);
    await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
    return { method: "manual", url, filename };
  }

  clickAnchor(url, filename);
  await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
  return { method: "download", url, filename };
}

/** Same delivery guarantees for text payloads (GeoJSON, DXF, CSV, JSON). */
export async function saveTextFile(
  content: string,
  filename: string,
  mime: string,
): Promise<SavedFile> {
  return saveFile(new Blob([content], { type: mime }), filename);
}

/** Same delivery guarantees for canvas/data-URL exports (PNG map exhibits). */
export async function saveDataUrl(dataUrl: string, filename: string): Promise<SavedFile> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return saveFile(blob, filename);
}

/** Human-readable note for a non-standard delivery route. */
export function saveMethodNotice(result: SavedFile): string | null {
  if (result.method === "download") return null;
  if (result.method === "new-tab") {
    return "Your browser opened the file in a new tab. Use Save there to keep it.";
  }
  return "Your browser blocked the download. Use the Open file link to save it.";
}
